import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, ScrollText, ArrowUpRight, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const EASE = [0.22, 1, 0.36, 1];

const DOCS = [
  {
    title: "Self-Study Batch",
    subtitle: "Terms and Conditions of Service",
    note: "₹49/month · daily 40 MCQ, monthly 1 OMR 150 MCQ mock test",
    file: "/assets/terms-selfstudy-batch.pdf",
    fileLabel: "View PDF",
    kind: "PDF",
    testid: "terms-selfstudy",
  },
  {
    title: "Focused Batch",
    subtitle: "Terms and Conditions of Service",
    note: "₹199/month · daily MCQs, tracking & customised tests",
    file: "/assets/terms-focused-batch.docx",
    fileLabel: "Download DOCX",
    kind: "DOCX",
    testid: "terms-focused",
  },
];

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 pb-28 pt-32 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700"
        >
          <ScrollText size={13} /> Legal
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
          className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl"
        >
          Terms &amp; Conditions
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-5 max-w-xl text-base leading-relaxed text-slate-600"
        >
          One document per batch — read the terms for the batch you are joining before you enroll.
        </motion.p>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {DOCS.map((d, i) => (
            <motion.a
              key={d.title}
              href={d.file}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.1, duration: 0.7, ease: EASE }}
              whileHover={{ y: -4 }}
              data-testid={d.testid}
              className="group flex flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-colors duration-200 hover:border-sky-300 hover:bg-sky-50/50"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-700 text-white">
                  {d.kind === "PDF" ? <FileText size={20} /> : <Download size={20} />}
                </span>
                <span className="rounded-full bg-sky-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-sky-800">
                  {d.kind}
                </span>
              </div>
              <h2 className="mt-6 font-display text-xl font-bold text-slate-900 sm:text-2xl">{d.title}</h2>
              <p className="mt-1 text-sm font-medium text-slate-500">{d.subtitle}</p>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{d.note}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-sky-700 transition-colors duration-200 group-hover:text-sky-900">
                {d.fileLabel}
                <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </motion.a>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.8, ease: EASE }}
          className="mt-10 rounded-3xl border border-sky-100 bg-sky-50/60 p-8 text-center"
          data-testid="terms-privacy-section"
        >
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700">Privacy</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Your data matters as much as your preparation. Read how we handle it.
          </p>
          <Link
            to="/privacy"
            data-testid="terms-privacy-link"
            className="group mt-5 inline-flex items-center gap-2 rounded-full bg-sky-700 px-7 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800"
          >
            Read our Privacy Policy
            <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.8 }}
          className="mt-10 text-center text-xs text-slate-500"
        >
          Questions about the terms? WhatsApp 77519 46736 (message only) or email hi.jyotiacademy@gmail.com
        </motion.p>
      </main>
      <Footer />
    </div>
  );
}
